import { Buffer } from "buffer";
import qs from "qs";
import axios from "../helpers/axios";
import { SanctionsOutcomeType } from "../screens/sanctionsOutcome";
import { DepositBankDetail } from "../types/depositBankDetail";
import {
  UserType,
  UserMambuDetails,
  VeriffSessionType,
  AuditLogType,
  KycAttributeType,
  AliasType,
  EddaAccountType,
} from "../types/user";
import { MambuAuthorizationHold, CardTokenResponse } from "../types";
import { CardDetail } from "../types/CardDetail";

export type SearchQuery = {
  name?: string;
  id?: string;
  mobile?: string;
  email?: string;
  appCountry?: string;
};

type GetUsersQuery = {
  page: number;
  pageSize: number;
  searchQuery?: SearchQuery | null;
};

export const getUsers = async ({
  page,
  pageSize,
  searchQuery,
}: GetUsersQuery) => {
  const queryString = page
    ? `?${qs.stringify({ page, pageSize, ...searchQuery })}`
    : "";
  const { data } = await axios.get(`/users${queryString}`);
  return data;
};

export const getUser = async (userId: string): Promise<UserType> => {
  const { data } = await axios.get(`/users/${userId}`);
  return data;
};

export const getDepositBankDetail = async (
  userId: string
): Promise<DepositBankDetail[]> => {
  const { data } = await axios.get(`/users/${userId}/get-deposit-bank-details`);
  return data;
};

export const getCardDetailByUserId = async (
  userId: string
): Promise<CardDetail> => {
  const { data } = await axios.get(`/card/summary/${userId}`);
  return data;
};

export const getUserMambuDetails = async (
  userId: string
): Promise<UserMambuDetails> => {
  const { data } = await axios.get(`/users/${userId}/mambu-details`);
  return data;
};

type VeriffProofOfAddressQuery = {
  url: string;
  headers: Record<string, string>;
};

export const getProofOfAddress = (sessionId: string) =>
  axios
    .get<VeriffProofOfAddressQuery>(`/kyc/proof-of-address-media/${sessionId}`)
    .then(({ data: query }) =>
      axios.get(query.url, {
        headers: query.headers,
        responseType: "arraybuffer",
      })
    )
    .then((response) => {
      return Buffer.from(response.data, "binary").toString("base64");
    });

export const approveProofOfAddress = (
  sessionId: UserType["session_id"]
): Promise<void> =>
  axios.post(
    "/kyc/approve-proof-of-address",
    {
      sessionId: sessionId,
      decision: "accept",
    },
    { validateStatus: (status) => status === 204 }
  );

export const updateSanctionsOutcome = (
  sessionId: UserType["session_id"],
  sanctionsOutcome: SanctionsOutcomeType
): Promise<void> =>
  axios.post("/kyc/update-sanctions-outcome", {
    sessionId,
    sanctionsOutcome,
  });

export const getVeriffSessions = async (
  userId: string
): Promise<VeriffSessionType[]> => {
  const { data } = await axios.get(`get-veriff-sessions-by-user/${userId}`);
  return data;
};

export const getAliasesByUserId = async (
  userId: string
): Promise<AliasType[]> => {
  const { data } = await axios.get(`users/${userId}/collection-aliases`);
  return data;
};

export const getTransactionHolds = async (
  userId: string,
  currency: string
): Promise<MambuAuthorizationHold[]> => {
  const { data } = await axios.get(`/users/${userId}/transaction-holds`, {
    params: { currency },
  });
  return data;
};

export const getAuditLog = async (
  userId: string,
  kycAttribute: KycAttributeType
): Promise<AuditLogType[]> => {
  const { data } = await axios.get(`/kyc/audit/${kycAttribute}/${userId}`);
  return data;
};

export const disableAccount = async (
  userId: string,
  loginOnly: boolean
): Promise<void> => {
  const { data } = await axios.get(
    `/users/${userId}/disable?loginOnly=${loginOnly}`
  );
  return data;
};

export const enableAccount = async (userId: string): Promise<void> => {
  const { data } = await axios.post(`/users/${userId}/enable`);
  return data;
};

export const getEddaAccountsByUserId = async (
  userId: string
): Promise<EddaAccountType[]> => {
  const { data } = await axios.get(`/users/${userId}/edda-accounts`);
  return data;
};

export const unlinkEddaAccount = async (
  userId: string,
  transactionId: string
): Promise<void> => {
  await axios.delete(`/users/${userId}/edda-accounts/${transactionId}`);
};

export const addCollectionAlias = async (
  userId: string,
  aliasName: string,
  driveLink?: string
): Promise<AliasType> => {
  const { data } = await axios.post(`/users/${userId}/collection-alias`, {
    aliasName,
    proofOfDocument: driveLink,
  });
  return data;
};

export const getCardTokens = async (
  userId: string
): Promise<CardTokenResponse> => {
  const { data } = await axios.get(`/users/${userId}/card-tokens`);
  return data;
};

export const manuallyActivate = async (
  userId: string,
  token: string,
  reason: string
): Promise<void> => {
  const { data } = await axios.put(
    `/users/${userId}/card-tokens/${token}/activate`,
    { reason }
  );
  return data;
};

export const deactivateCardToken = async (
  userId: string,
  token: string,
  reason: string
): Promise<void> => {
  const { data } = await axios.put(
    `/users/${userId}/card-tokens/${token}/deactivate`,
    { reason }
  );
  return data;
};

export const terminateCard = async (
  cardOpaqueId: string,
  cardNumberOpaqueId: string
): Promise<void> => {
  await axios.delete(
    `/cards/${cardOpaqueId}/card-numbers/${cardNumberOpaqueId}`
  );
};

export type CustomerAddressUpdate = {
  addr_line_1: string;
  addr_line_2: string;
  addr_line_3: string;
  addr_postal: string;
};

export const updateUserAddress = async (
  userId: string,
  address: CustomerAddressUpdate
): Promise<void> => {
  const { data } = await axios.put(`/users/${userId}/address`, address);
  return data;
};

export const updateUserLegalName = async (
  userId: string,
  fullName: string
): Promise<void> => {
  const { data } = await axios.put(`/users/${userId}/legal-name`, {
    full_name: fullName,
  });
  return data;
};

export const updateMobileNumber = async (
  userId: string,
  countryCode: string,
  mobileNumber: string
): Promise<void> => {
  const { data } = await axios.put(`/users/${userId}/mobile`, {
    mobile_country_code: countryCode,
    mobile: mobileNumber,
  });
  return data;
};

export const clearMobileNumber = async (userId: string): Promise<void> => {
  const { data } = await axios.delete(`/users/${userId}/mobile`);
  return data;
};

// export const getEddaAccountsByUserId = async (
//   userId: string
// ): Promise<EddaAccountType[]> => {
//   const { data } = await axios.get(`/users/${userId}/edda-accounts`);
//   return data;
// };
